import '../../render'
import {RENDER_FORMATS, STATUS_TYPES} from '../../render/constants'
import {getCanvas, getPDFPromise} from './common'

import type pdfjs from 'pdfjs-dist'
import type {PDFDocumentProxy} from 'pdfjs-dist'
import Status from '../../render/status'

/**
 * This class was created due to canvas memory limitations on iOS,
 * which requires us to only render a single canvas with one page
 * at a time.
 *
 * Ideally all PDFs would be rendered using the MultiPageView class
 * as it provides a better user experience, so only use this if the
 * device you're using is running into memory issues.
 */
class SinglePageView {
  private status = new Status(RENDER_FORMATS.pdf)
  private shell = document.querySelector<HTMLElement>('.js-render-shell')!
  private pageContainer = this.shell.querySelector<HTMLElement>('.pdf-container')!
  private pageNav = this.shell.querySelector<HTMLElement>('#single-page-nav')!

  private currentPage = 1
  private pagePrev = this.pageNav.querySelector<HTMLElement>('#page-prev')!
  private pageNext = this.pageNav.querySelector<HTMLElement>('#page-next')!

  // How many pages the document contains
  private totalPages = 0

  private pagesRendered = 0

  private pdf!: PDFDocumentProxy

  constructor(private PDFJS: typeof pdfjs) {
    // Hide by default, only showing when there are multiple pages
    this.pageNav.style.display = 'none'

    // Load PDFJS worker from correct path
    const workerSrc = this.pageContainer.getAttribute('data-pdf-worker-js-path')

    if (!workerSrc) {
      this.status.set(STATUS_TYPES.error, {error: 'Missing pdf worker src path'})
      return
    }

    this.PDFJS.GlobalWorkerOptions.workerSrc = workerSrc

    this.pagePrev.addEventListener('click', () => this.renderPage(this.currentPage - 1))
    this.pageNext.addEventListener('click', () => this.renderPage(this.currentPage + 1))

    window.addEventListener('resize', () => {
      // Prevent resize jank on pjax navigations
      // when a user goes back and forth using browser navigation,
      // the iframe is getting resized as DOM classes are added / removed by code on dotcom
      // There seems to be a race condition where this handler is listening
      // before those classes have been updated, which triggers a resize of 1px
      if (this.pagesRendered > 0) {
        this.status.set(STATUS_TYPES.resize, {height: this.entireHeight()})
      }
    })

    this.init()
  }

  private init = async () => {
    const url = this.pageContainer.getAttribute('data-file') ?? ''

    try {
      await this.fetch(url)
      await this.renderPage(this.currentPage)
      const height = this.entireHeight()
      this.status.set(STATUS_TYPES.ready, {height})
      // For some reason the height of the iframe doesn't update until a few hundred milliseconds after the page is loaded, so we need to wait for that
      const interval = setInterval(() => {
        if (height !== this.entireHeight()) {
          this.status.set(STATUS_TYPES.resize, {height: this.entireHeight()})
          clearInterval(interval)
        }
      }, 100)
    } catch (error) {
      this.status.set(STATUS_TYPES.error, {error})
    }
  }

  private entireHeight = () => document.documentElement.scrollHeight

  private fetch = async (url: string) => {
    window.debug(`Attempting to load [${url}]`)
    this.status.set(STATUS_TYPES.loading)

    try {
      this.pdf = await getPDFPromise(url, this.pageContainer.getAttribute('data-bcmap-dir')!)
      this.totalPages = this.pdf.numPages
      window.debug(`Got PDF with ${this.totalPages} pages`)
      if (this.totalPages > 1) {
        this.pageNav.style.display = 'block'
      }
      this.status.set(STATUS_TYPES.loaded)
    } catch (reason) {
      window.debug(`Invalid PDF because ${reason}`)
      this.status.set(STATUS_TYPES.invalid)
    }
  }

  async renderPage(num: number) {
    if (num < 1 || num > this.totalPages) return
    this.currentPage = num
    // Deload all pages first to prevent memory issues
    for (const canvas of document.querySelectorAll<HTMLCanvasElement>('.pdf-page')) {
      canvas.width = 0
      canvas.height = 0

      canvas.remove()
    }

    const page = await this.pdf.getPage(this.currentPage)

    const [canvas, error] = await getCanvas(page)
    if (error !== null) {
      this.status.set(STATUS_TYPES.fatal, {
        error: `Error loading PDF page number ${page.pageNumber}`,
      })
      throw error
    }

    this.pageContainer.append(canvas)

    this.pagePrev.setAttribute('aria-disabled', String(this.currentPage <= 1))
    this.pageNext.setAttribute('aria-disabled', String(this.currentPage >= this.totalPages))

    window.debug(`Page ${this.currentPage} ready`)
  }
}

export default SinglePageView
